var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import classNames from 'classnames';
import shallowEqual from 'shallowequal';
import useMergedState from 'rc-util/lib/hooks/useMergedState';
import warning from 'rc-util/lib/warning';
import Overflow from 'rc-overflow';
import { flushSync } from 'react-dom';
import MenuItem from './MenuItem';
import { parseChildren } from './utils/nodeUtil';
import MenuContextProvider from './context/MenuContext';
import useMemoCallback from './hooks/useMemoCallback';
import { warnItemProp } from './utils/warnUtil';
import SubMenu from './SubMenu';
import useAccessibility from './hooks/useAccessibility';
import useUUID from './hooks/useUUID';
import { PathRegisterContext, PathUserContext } from './context/PathContext';
import useKeyRecords, { OVERFLOW_KEY } from './hooks/useKeyRecords';
import { IdContext } from './context/IdContext';
/**
 * Menu modify after refactor:
 * ## Add
 * - disabled
 *
 * ## Remove
 * - openTransitionName
 * - openAnimation
 * - onDestroy
 * - siderCollapsed: Seems antd do not use this prop (Need test in antd)
 * - collapsedWidth: Seems this logic should be handle by antd Layout.Sider
 */
// optimize for render
const EMPTY_LIST = [];
const Menu = props => {
    var _a;
    const _b = props, { prefixCls = 'rc-menu', style, className, tabIndex = 0, children, direction, id, 
    // Mode
    mode = 'vertical', inlineCollapsed, 
    // Disabled
    disabled, disabledOverflow, 
    // Open
    subMenuOpenDelay = 0.1, subMenuCloseDelay = 0.1, forceSubMenuRender, defaultOpenKeys, openKeys, 
    // Active
    activeKey, defaultActiveFirst, 
    // Selection
    selectable = true, multiple = false, defaultSelectedKeys, selectedKeys, onSelect, onDeselect, 
    // Level
    inlineIndent = 14, 
    // Motion
    motion, defaultMotions, 
    // Popup
    triggerSubMenuAction = 'hover', builtinPlacements, 
    // Icon
    itemIcon, expandIcon, overflowedIndicator = '...', overflowedIndicatorPopupClassName, 
    // Function
    getPopupContainer, 
    // Events
    onClick, onOpenChange, onKeyDown, 
    // Deprecated
    openAnimation, openTransitionName } = _b, restProps = __rest(_b, ["prefixCls", "style", "className", "tabIndex", "children", "direction", "id", "mode", "inlineCollapsed", "disabled", "disabledOverflow", "subMenuOpenDelay", "subMenuCloseDelay", "forceSubMenuRender", "defaultOpenKeys", "openKeys", "activeKey", "defaultActiveFirst", "selectable", "multiple", "defaultSelectedKeys", "selectedKeys", "onSelect", "onDeselect", "inlineIndent", "motion", "defaultMotions", "triggerSubMenuAction", "builtinPlacements", "itemIcon", "expandIcon", "overflowedIndicator", "overflowedIndicatorPopupClassName", "getPopupContainer", "onClick", "onOpenChange", "onKeyDown", "openAnimation", "openTransitionName"]);
    const childList = parseChildren(children, EMPTY_LIST);
    const [mounted, setMounted] = React.useState(false);
    const containerRef = React.useRef();
    const uuid = useUUID(id);
    const isRtl = direction === 'rtl';
    // ========================= Warn =========================
    if (process.env.NODE_ENV !== 'production') {
        warning(!openAnimation && !openTransitionName, '`openAnimation` and `openTransitionName` is removed. Please use `motion` or `defaultMotion` instead.');
    }
    // ========================= Open =========================
    const [mergedOpenKeys, setMergedOpenKeys] = useMergedState(defaultOpenKeys, {
        value: openKeys,
        postState: keys => keys || EMPTY_LIST
    });
    // React 18 will merge mouse event which means we open key will not sync
    // ref: https://github.com/ant-design/ant-design/issues/38818
    const triggerOpenKeys = (keys, forceFlush = false) => {
        function doUpdate() {
            setMergedOpenKeys(keys);
            onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(keys);
        }
        if (forceFlush) {
            flushSync(doUpdate);
        }
        else {
            doUpdate();
        }
    };
    // >>>>> Cache & Reset open keys when inlineCollapsed changed
    const [inlineCacheOpenKeys, setInlineCacheOpenKeys] = React.useState(mergedOpenKeys);
    const mountRef = React.useRef(false);
    // ========================= Mode =========================
    const [mergedMode, mergedInlineCollapsed] = React.useMemo(() => {
        if ((mode === 'inline' || mode === 'vertical') && inlineCollapsed) {
            return ['vertical', inlineCollapsed];
        }
        return [mode, false];
    }, [mode, inlineCollapsed]);
    const isInlineMode = mergedMode === 'inline';
    const [internalMode, setInternalMode] = React.useState(mergedMode);
    const [internalInlineCollapsed, setInternalInlineCollapsed] = React.useState(mergedInlineCollapsed);
    React.useEffect(() => {
        setInternalMode(mergedMode);
        setInternalInlineCollapsed(mergedInlineCollapsed);
        if (!mountRef.current) {
            return;
        }
        // Synchronously update MergedOpenKeys
        if (isInlineMode) {
            setMergedOpenKeys(inlineCacheOpenKeys);
        }
        else {
            // Trigger open event in case its in control
            triggerOpenKeys(EMPTY_LIST);
        }
    }, [mergedMode, mergedInlineCollapsed]);
    // ====================== Responsive ======================
    const [lastVisibleIndex, setLastVisibleIndex] = React.useState(0);
    const allVisible = lastVisibleIndex >= childList.length - 1
        || internalMode !== 'horizontal'
        || disabledOverflow;
    // Cache
    React.useEffect(() => {
        if (isInlineMode) {
            setInlineCacheOpenKeys(mergedOpenKeys);
        }
    }, [mergedOpenKeys]);
    React.useEffect(() => {
        mountRef.current = true;
        return () => {
            mountRef.current = false;
        };
    }, []);
    // ========================= Path =========================
    const { registerPath, unregisterPath, refreshOverflowKeys, isSubPathKey, getKeyPath, getKeys, getSubPathKeys } = useKeyRecords();
    const registerPathContext = React.useMemo(() => ({ registerPath, unregisterPath }), [registerPath, unregisterPath]);
    const pathUserContext = React.useMemo(() => ({ isSubPathKey }), [isSubPathKey]);
    React.useEffect(() => {
        refreshOverflowKeys(allVisible
            ? EMPTY_LIST
            : childList
                .slice(lastVisibleIndex + 1)
                .map(child => child.key));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastVisibleIndex, allVisible]);
    // ======================== Active ========================
    const [mergedActiveKey, setMergedActiveKey] = useMergedState(activeKey || (defaultActiveFirst && ((_a = childList[0]) === null || _a === void 0 ? void 0 : _a.key)), {
        value: activeKey,
    });
    const onActive = useMemoCallback((key) => {
        setMergedActiveKey(key);
    });
    const onInactive = useMemoCallback(() => {
        setMergedActiveKey(undefined);
    });
    // ======================== Select ========================
    // >>>>> Select keys
    const [mergedSelectKeys, setMergedSelectKeys] = useMergedState(defaultSelectedKeys || [], {
        value: selectedKeys,
        // Legacy convert key to array
        postState: keys => {
            if (Array.isArray(keys)) {
                return keys;
            }
            if (keys === null || keys === undefined) {
                return EMPTY_LIST;
            }
            return [keys];
        }
    });
    // >>>>> Trigger select
    const triggerSelection = (info) => {
        if (selectable) {
            // Insert or Remove
            const { key: targetKey } = info;
            const exist = mergedSelectKeys.includes(targetKey);
            let newSelectKeys;
            if (multiple) {
                if (exist) {
                    newSelectKeys = mergedSelectKeys.filter(key => key !== targetKey);
                }
                else {
                    newSelectKeys = [...mergedSelectKeys, targetKey];
                }
            }
            else {
                newSelectKeys = [targetKey];
            }
            setMergedSelectKeys(newSelectKeys);
            // Trigger event
            const selectInfo = Object.assign(Object.assign({}, info), { selectedKeys: newSelectKeys });
            if (exist) {
                onDeselect === null || onDeselect === void 0 ? void 0 : onDeselect(selectInfo);
            }
            else {
                onSelect === null || onSelect === void 0 ? void 0 : onSelect(selectInfo);
            }
        }
        // Whatever selectable, always close it
        if (!multiple && mergedOpenKeys.length && internalMode !== 'inline') {
            triggerOpenKeys(EMPTY_LIST);
        }
    };
    // ========================= Open =========================
    /**
     * Click for item. SubMenu do not have selection status
     */
    const onInternalClick = useMemoCallback((info) => {
        onClick === null || onClick === void 0 ? void 0 : onClick(warnItemProp(info));
        triggerSelection(info);
    });
    const onInternalOpenChange = useMemoCallback((key, open) => {
        let newOpenKeys = mergedOpenKeys.filter(k => k !== key);
        if (open) {
            newOpenKeys.push(key);
        }
        else if (internalMode !== 'inline') {
            // We need find all related popup to close
            const subPathKeys = getSubPathKeys(key);
            newOpenKeys = newOpenKeys.filter(k => !subPathKeys.has(k));
        }
        if (!shallowEqual(mergedOpenKeys, newOpenKeys)) {
            triggerOpenKeys(newOpenKeys, true);
        }
    });
    const getInternalPopupContainer = useMemoCallback(getPopupContainer);
    // ==================== Accessibility =====================
    const triggerAccessibilityOpen = (key, open) => {
        const nextOpen = open !== null && open !== void 0 ? open : !mergedOpenKeys.includes(key);
        onInternalOpenChange(key, nextOpen);
    };
    const onInternalKeyDown = useAccessibility(internalMode, mergedActiveKey, isRtl, uuid, containerRef, getKeys, getKeyPath, setMergedActiveKey, triggerAccessibilityOpen, onKeyDown);
    // ======================== Effect ========================
    React.useEffect(() => {
        setMounted(true);
    }, []);
    // ======================== Render ========================
    // >>>>> Children
    const wrappedChildList = internalMode !== 'horizontal' || disabledOverflow
        ? childList
        // Need wrap for overflow dropdown that do not response for open
        : childList.map((child, index) => (
        // Always wrap provider to avoid sub node re-mount
        <MenuContextProvider key={child.key} overflowDisabled={index > lastVisibleIndex}>
                    {child}
                </MenuContextProvider>));
    // >>>>> Container
    const container = (<Overflow id={id} ref={containerRef} prefixCls={`${prefixCls}-overflow`} component="ul" itemComponent={MenuItem} className={classNames(prefixCls, `${prefixCls}-root`, `${prefixCls}-${internalMode}`, className, {
            [`${prefixCls}-inline-collapsed`]: internalInlineCollapsed,
            [`${prefixCls}-rtl`]: isRtl
        })} dir={direction} style={style} role="menu" tabIndex={tabIndex} data={wrappedChildList} renderRawItem={node => node} renderRawRest={omitItems => {
            // We use origin list since wrapped list use context to prevent open
            const len = omitItems.length;
            const originOmitItems = len ? childList.slice(-len) : null;
            return (<SubMenu eventKey={OVERFLOW_KEY} title={overflowedIndicator} disabled={allVisible} internalPopupClose={len === 0} popupClassName={overflowedIndicatorPopupClassName}>
                        {originOmitItems}
                    </SubMenu>);
        }} maxCount={internalMode !== 'horizontal' || disabledOverflow
            ? Overflow.INVALIDATE
            : Overflow.RESPONSIVE} ssr="full" data-menu-list onVisibleChange={newLastIndex => {
            setLastVisibleIndex(newLastIndex);
        }} onKeyDown={onInternalKeyDown} {...restProps}/>);
    // >>>>> Render
    return (<IdContext.Provider value={uuid}>
            <MenuContextProvider prefixCls={prefixCls} mode={internalMode} inlineCollapsed={mergedInlineCollapsed} openKeys={mergedOpenKeys} rtl={isRtl} 
    // Disabled
    disabled={disabled} 
    // Motion
    motion={mounted ? motion : null} defaultMotions={mounted ? defaultMotions : null} 
    // Active
    activeKey={mergedActiveKey} onActive={onActive} onInactive={onInactive} 
    // Selection
    selectedKeys={mergedSelectKeys} 
    // Level
    inlineIndent={inlineIndent} 
    // Popup
    subMenuOpenDelay={subMenuOpenDelay} subMenuCloseDelay={subMenuCloseDelay} forceSubMenuRender={forceSubMenuRender} builtinPlacements={builtinPlacements} triggerSubMenuAction={triggerSubMenuAction} getPopupContainer={getInternalPopupContainer} 
    // Icon
    itemIcon={itemIcon} expandIcon={expandIcon} 
    // Events
    onItemClick={onInternalClick} onOpenChange={onInternalOpenChange}>
                <PathUserContext.Provider value={pathUserContext}>
                    {container}
                </PathUserContext.Provider>

                {/* Measure menu keys. Add `display: none` to avoid some developer miss use the Menu */}
                <div style={{ display: 'none' }} aria-hidden>
                    <PathRegisterContext.Provider value={registerPathContext}>
                        {childList}
                    </PathRegisterContext.Provider>
                </div>
            </MenuContextProvider>
        </IdContext.Provider>);
};
export default Menu;
